import type { AxiosError } from 'axios';
import { Button, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { PhysicalObject } from '@/api/generated/models';
import {
  getGetObjectQueryKey,
  getListObjectsQueryKey,
  getSearchObjectsQueryKey,
  useCreateObject,
  useUpdateObject
} from '@/api/generated/objects/objects';
import {
  FormModal,
  ModalFooter,
  MutationButton,
  SelectField,
  TextareaField,
  TextField,
  Toast
} from '@/components/common';
import { OBJECT_TYPES } from '@/features/price-lists/objectDisplay';

const baseSchema = z.object({
  objectType: z.string().min(1),
  objectCode: z.string().min(1),
  objectName: z.string().min(1),
  description: z.string().min(1),
  externalReference: z.string().optional(),
  location: z.string().optional()
});

type FormValues = z.infer<typeof baseSchema>;

const emptyValues: FormValues = {
  objectType: '',
  objectCode: '',
  objectName: '',
  description: '',
  externalReference: '',
  location: ''
};

// Isti obrazac kao PriceListFormModal — backend već vraća specifičan,
// lokalizirani razlog (npr. "Šifra objekta već postoji...").
const objectErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

interface ObjectFormModalProps {
  opened: boolean;
  onClose: () => void;
  object?: PhysicalObject;
  existingObjectCodes?: string[];
  onCreated?: (id: string) => void;
  onSaved?: (message: string) => void;
}

export const ObjectFormModal = ({
  opened,
  onClose,
  object,
  existingObjectCodes = [],
  onCreated,
  onSaved
}: ObjectFormModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!object;

  // Duplikat šifre mora biti dio SHEME (superRefine), ne provjera unutar
  // handleSubmit — vidi PriceListItemFormModal za isto obrazloženje. Backend
  // je i dalje konačan izvor istine (uq_objects_owner_code).
  const schema = baseSchema.superRefine((data, ctx) => {
    if (!isEdit && existingObjectCodes.includes(data.objectCode.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['objectCode'],
        message: t('objectRegistry.codeDuplicate')
      });
    }
  });

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: emptyValues
  });

  useEffect(() => {
    if (opened) {
      form.setValues(
        object
          ? {
              objectType: object.objectType ?? '',
              objectCode: object.objectCode ?? '',
              objectName: object.objectName ?? '',
              description: object.description ?? '',
              externalReference: object.externalReference ?? '',
              location: object.location ?? ''
            }
          : emptyValues
      );
    }
  }, [opened, object]);

  const createMutation = useCreateObject({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListObjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getSearchObjectsQueryKey() });
        const createdId = (created as PhysicalObject | undefined)?.id;
        onSaved?.(t('objectRegistry.createSuccess'));
        if (createdId) onCreated?.(createdId);
      }
    }
  });

  const updateMutation = useUpdateObject({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListObjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getSearchObjectsQueryKey() });
        if (object?.id) {
          queryClient.invalidateQueries({ queryKey: getGetObjectQueryKey(object.id) });
        }
        onSaved?.(t('objectRegistry.updateSuccess'));
        onClose();
      }
    }
  });

  const mutation = isEdit ? updateMutation : createMutation;

  const handleSubmit = (values: FormValues) => {
    if (isEdit && object?.id) {
      updateMutation.mutate({
        id: object.id,
        data: {
          objectType: values.objectType,
          objectName: values.objectName,
          description: values.description,
          externalReference: values.externalReference,
          location: values.location
        }
      });
    } else {
      createMutation.mutate({
        data: {
          objectType: values.objectType,
          objectCode: values.objectCode,
          objectName: values.objectName,
          description: values.description,
          externalReference: values.externalReference,
          location: values.location
        }
      });
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={onClose}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {isEdit ? t('objectRegistry.editObject') : t('objectRegistry.newObject')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={16}>
          <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
            {t('objectRegistry.formIntro')}
          </Text>

          {mutation.isError && (
            <Toast tone="danger">{objectErrorMessage(mutation.error, t('common.error'))}</Toast>
          )}

          <div className="form-row-2col">
            <div style={{ flex: 1 }}>
              <SelectField
                label={t('objectRegistry.fieldType')}
                required
                allowDeselect={false}
                placeholder={t('objectRegistry.typeSelectPlaceholder')}
                data={OBJECT_TYPES.map((type) => ({ value: type.value, label: t(type.labelKey) }))}
                {...form.getInputProps('objectType')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                label={t('objectRegistry.fieldCode')}
                required
                disabled={isEdit}
                placeholder={t('objectRegistry.codePlaceholder')}
                {...form.getInputProps('objectCode')}
              />
            </div>
          </div>

          <TextField
            label={t('objectRegistry.fieldName')}
            required
            {...form.getInputProps('objectName')}
          />

          <TextareaField
            label={t('objectRegistry.fieldDescription')}
            required
            minRows={3}
            placeholder={t('objectRegistry.descriptionPlaceholder')}
            {...form.getInputProps('description')}
          />

          <div className="form-row-2col">
            <div style={{ flex: 1 }}>
              <TextField
                label={t('objectRegistry.fieldLocation')}
                {...form.getInputProps('location')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                label={t('objectRegistry.fieldExternalRef')}
                {...form.getInputProps('externalReference')}
              />
            </div>
          </div>

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <MutationButton
              type="submit"
              isPending={mutation.isPending}
              isPaused={mutation.isPaused}
            >
              {t('objectRegistry.saveObject')}
            </MutationButton>
          </ModalFooter>
        </Stack>
      </form>
    </FormModal>
  );
};
